/**
 * ISS-031: read-only listing of permanent pg_cron failures.
 *
 * Sourced from the `cron_failures` table created by PR #522 (ISS-032 —
 * SQLSTATE specificity in tick_turnover_units). Cron failures are
 * permanent by definition — there's no retry primitive — so this card
 * is read-only. Click a row → open StalledRowDrawer for the full
 * SQLSTATE / message / context.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { listStalledRows } from '@/lib/pocketSettingsApi';
import type { CronStalledRow } from '@/types/pocketSettings';

interface Props {
  onRowClick: (row: CronStalledRow) => void;
  reloadKey?: number;
}

const PAGE_SIZE = 25;

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(s: string | null, n = 80): string {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function CronFailuresCard({ onRowClick, reloadKey = 0 }: Props) {
  const [rows, setRows] = useState<CronStalledRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listStalledRows({ source: 'cron', limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows as CronStalledRow[]);
        setTotal(res.total);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load cron failures.");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Cron failures</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Permanent (non-transient) pg_cron job failures. Read-only — these
          rows are diagnostic; cron failures don't have retry semantics. Transient
          failures (lock_not_available, deadlock_detected, serialization_failure)
          self-heal on the next tick and aren't recorded here.
        </p>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No cron failures recorded. (This is the expected steady state.)
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>SQLSTATE</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right hidden md:table-cell">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => onRowClick(row)}
                    className="cursor-pointer hover:bg-muted/30"
                  >
                    <TableCell className="font-mono text-xs">{row.job_name}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {row.sqlstate ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs max-w-md truncate">
                      {truncate(row.message)}
                    </TableCell>
                    <TableCell className="text-right text-[11px] text-muted-foreground tabular-nums hidden md:table-cell whitespace-nowrap">
                      {formatDate(row.raised_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > rows.length && (
              <p className="text-[11px] text-muted-foreground mt-3 text-right tabular-nums">
                Showing {rows.length} of {total}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
